import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { Ref } from 'react';
import { usePlayerStore } from '../../stores/player-store';
import { useUserStore } from '../../stores/user-store';
import { logger } from '../logging/logger';
import SpinnerLoading from './SpinnerLoading';
import './Mp3Player.scss';

// Port of src/app/shared/components/mp3-player/ (mypart-mp3-player).
//
// Angular contract → React:
// - @Input audioUrl → prop; @Output playSongClicked → onPlaySongClicked prop
// - host [ngClass] from call sites ('p-4' when highlighted) → className prop
//   on a host-equivalent wrapper div
// - ResultItem called the public playUrl() via @ViewChild → imperative handle.
// - effect #1: another mp3 started (playingAudioUrl !== ours) → hide controls.
//   effect #2: a YouTube iframe started (playingIframeId) → hide controls,
//   pause and rewind. Angular called audio.fastSeek(0) directly; fastSeek is
//   not implemented in Chromium, so the call is optional-chained here
//   (audio.fastSeek?.(0)) instead of throwing — same outcome on Safari/Firefox.
// - songIsPlaying was a plain private field → ref (no re-render needed).
// - data-play-target marks the clickable container for Player.tsx next/prev
//   navigation (replaces Angular's firstChild DOM traversal).

export interface Mp3PlayerHandle {
  playUrl: () => void;
}

export interface Mp3PlayerProps {
  audioUrl?: string;
  onPlaySongClicked: () => void;
  /** Classes the Angular call sites put on the <mypart-mp3-player> host. */
  className?: string;
  /**
   * WL preload window: when true this card is in the rolling window and the
   * browser is hinted to fully buffer the MP3 (preload="auto"); otherwise
   * preload="none". See useMp3PreloadWindow. Default false.
   */
  preloadAudio?: boolean;
  ref?: Ref<Mp3PlayerHandle>;
}

export default function Mp3Player({ audioUrl, onPlaySongClicked, className, preloadAudio = false, ref }: Mp3PlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const songIsPlayingRef = useRef(false);
  // Whether the user intends THIS song to be playing. Drives the recovery retry:
  // if play() is aborted while the audio is still buffering (the "stuck on a
  // not-yet-loaded song" bug), we retry once the element reaches `canplay`.
  const wantsPlayRef = useRef(false);
  const [showAudio, setShowAudio] = useState(false);
  const [loading, setLoading] = useState(false);

  const playingAudioUrl = usePlayerStore(s => s.playingAudioUrl);
  const playingIframeId = usePlayerStore(s => s.playingIframeId);

  // NOTE(port): the Angular original only hid the controls here and left the
  // outgoing <audio> playing, so starting a second MP3 produced overlapping
  // audio (and the play()/pause() races logged as unhandledrejections). Stop
  // and rewind the element when another MP3 takes over — same teardown the
  // YouTube-takeover effect below already does.
  useEffect(() => {
    if (playingAudioUrl !== audioUrl) {
      setShowAudio(false);
      setLoading(false);
      wantsPlayRef.current = false;
      songIsPlayingRef.current = false;
      audioRef.current?.pause();
      audioRef.current?.fastSeek?.(0);
    }
  }, [playingAudioUrl, audioUrl]);

  useEffect(() => {
    if (playingIframeId) {
      setShowAudio(false);
      setLoading(false);
      wantsPlayRef.current = false;
      songIsPlayingRef.current = false;
      audioRef.current?.pause();
      audioRef.current?.fastSeek?.(0);
    }
  }, [playingIframeId]);

  // play() returns a promise that rejects ("The play() request was interrupted
  // by a call to pause()") whenever a pause()/load() lands before it resolves —
  // routine during rapid song switching. Swallow it so it doesn't surface as an
  // unhandledrejection. (Some legacy browsers return undefined from play().)
  const playSafely = () => {
    const promise = audioRef.current?.play();
    if (promise) {
      promise.catch((err: DOMException) => {
        // Requested playback did not start. NotAllowedError = autoplay blocked;
        // AbortError = superseded by a newer play/pause during rapid switching.
        logger.warn('playback', 'audio play did not start', {
          audioUrl,
          reason: err?.name,
          message: err?.message,
        });
      });
    }
  };

  // Native <audio> event handlers. Beyond mirroring state into our refs (they
  // must NOT re-issue play()/pause() on the element that fired the event — that
  // was the feedback loop behind the play()/pause() races), they also drive the
  // loading spinner and the recovery retry.
  const onPlay = () => {
    setShowAudio(true);
    songIsPlayingRef.current = true;
  };

  // Fires when buffering delays playback — show the spinner so a not-yet-loaded
  // song reads as "loading", not "stuck".
  const onWaiting = () => {
    if (wantsPlayRef.current) setLoading(true);
  };

  // The element has buffered enough to play. If the user still wants this song
  // and a transient pause()/abort left it paused (the stuck case), retry now.
  const onCanPlay = () => {
    if (wantsPlayRef.current && audioRef.current?.paused) {
      playSafely();
    }
  };

  // Real playback has begun — clear the spinner.
  const onPlaying = () => {
    setShowAudio(true);
    songIsPlayingRef.current = true;
    setLoading(false);
  };

  const onPause = () => {
    songIsPlayingRef.current = false;
    // A pause after playback has progressed is the user (or a takeover) pausing —
    // honor it and drop the intent so onCanPlay won't auto-resume. A pause at
    // currentTime 0 is the transient abort while still buffering: keep the intent
    // and the spinner so onCanPlay can recover the stuck song.
    if ((audioRef.current?.currentTime ?? 0) > 0) {
      wantsPlayRef.current = false;
      setLoading(false);
    }
  };

  const onEnded = () => {
    songIsPlayingRef.current = false;
    wantsPlayRef.current = false;
    setLoading(false);
  };

  const onError = () => {
    setLoading(false);
  };

  const playUrl = () => {
    if (songIsPlayingRef.current) {
      wantsPlayRef.current = false;
      setLoading(false);
      audioRef.current?.pause();
    } else {
      wantsPlayRef.current = true;
      setShowAudio(true);
      setLoading(true);
      playSafely();
    }

    if (!useUserStore.getState().user) {
      useUserStore.getState().checkUserHasExceededAllowedSongs();
    }

    usePlayerStore.getState().setPlayingAudio(audioUrl, audioRef.current ?? undefined);
    onPlaySongClicked();
  };

  useImperativeHandle(ref, () => ({ playUrl }));

  // WL preload-window cancellation: when this card unmounts (e.g. a new search
  // clears the song list via setSongs([])), explicitly abort any in-flight
  // preload rather than relying on the browser to drop it when the element is
  // removed (which is only best-effort).
  //
  // NOTE(port): our audio URL lives on a child <source>, not the <audio> src
  // attribute, so removeAttribute('src') alone is a no-op and a bare load()
  // would re-run resource selection against the surviving <source> and RESTART
  // the fetch. We therefore remove the <source> children first; load() then
  // selects no resource and aborts cleanly.
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (!audio) return;
      audio.pause();
      audio.removeAttribute('src');
      audio.querySelectorAll('source').forEach(source => source.remove());
      audio.load();
    };
  }, []);

  return (
    <div className={className}>
      <div
          data-play-target
          onClick={playUrl}
          className="absolute top-0 left-0 h-full w-full z-30 cursor-pointer flex">
        {loading && <SpinnerLoading />}
        <audio
            ref={audioRef}
            preload={preloadAudio ? 'auto' : 'none'}
            onCanPlay={onCanPlay}
            onEnded={onEnded}
            onError={onError}
            onPause={onPause}
            onPlay={onPlay}
            onPlaying={onPlaying}
            onWaiting={onWaiting}
            className={`audio-player${!showAudio ? ' hidden' : ''}`}
            controls>
          <source src={audioUrl} type="audio/mp3" />
          Your browser does not support the audio element.
        </audio>
      </div>
    </div>
  );
}
